/**
 * captureBulkConfirm — the confirm dialog copy for a BULK capture delete.
 *
 * WHY THIS FILE EXISTS (Gordon, QA-79, 2026-08-04)
 * "When multiple items are selected on the Capture page, the bottom floating
 * menu (currently 'N selected · Add to Wheel · Create Wheel') needs DELETE
 * (bulk delete) added as one of the options."
 *
 * G-163 — A BULK DELETE MUST CONFIRM AGAINST A NAMED SELECTION.
 * A bulk delete is the one action on this page that destroys several rows at
 * once, and the failure mode is a user who selected more than they thought. So
 * the dialog states the COUNT and lists what is going (up to a readable number,
 * then "+N more") — it never says a vague "Delete these items?". The count in
 * the title is what a test asserts on BEFORE the destructive click, so the
 * assertion cannot be satisfied by any page-wide text match.
 *
 * Pure — no dispatch, no DOM — so the wording is unit-testable and both the
 * count and the item naming are provable without driving a browser.
 */

import { captureDisplayName } from "./captureDisplayName";

/** How many item names to spell out before collapsing into "+N more". */
export const BULK_CONFIRM_NAME_LIMIT = 3;

/** Truncate one name so a single long note cannot swallow the dialog. */
const shorten = (name, max = 40) =>
  name.length > max ? `${name.slice(0, max - 1).trimEnd()}…` : name;

/**
 * Human list of what is about to be deleted: "a.png, b.png and 4 more".
 * Exported so the count/naming rule can be asserted on its own.
 */
export const bulkDeleteItemSummary = (items = []) => {
  const names = (items || []).filter(Boolean).map((it) => shorten(captureDisplayName(it)));
  if (names.length === 0) return "";
  const shown = names.slice(0, BULK_CONFIRM_NAME_LIMIT);
  const hidden = names.length - shown.length;
  if (hidden > 0) return `${shown.join(", ")} and ${hidden} more`;
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;
};

/**
 * Copy for the shared confirm dialog (`redux/reducers/dialog.openModal`).
 *
 * @param {object[]} items the SELECTED capture rows — never the whole list
 * @returns {{title: string, subtitle: string, note: string, confirmLabel: string}}
 */
export const bulkDeleteConfirmCopy = (items = []) => {
  const list = (items || []).filter(Boolean);
  const count = list.length;
  const plural = count === 1 ? "capture" : "captures";
  return {
    // The COUNT is in the title: the dialog names the size of the selection
    // before anything is destroyed (G-163).
    title: `Delete ${count} ${plural}?`,
    subtitle: `${bulkDeleteItemSummary(list)} will be permanently removed from your inbox.`,
    note: "This action cannot be undone.",
    confirmLabel: "Delete",
  };
};

/**
 * What to tell the user after a bulk delete finishes.
 *
 * There is no bulk-delete endpoint (`services/capture.deleteCaptureItems` fans
 * out over the single-item DELETE), so a PARTIAL outcome is a real state, not a
 * theoretical one — and reporting a partial as a flat success is exactly the
 * lie this helper exists to prevent.
 *
 * @param {{deletedIds: Array, failedIds: Array}} result
 * @returns {{tone: 'success'|'warning'|'error', message: string}}
 */
/**
 * Confirm copy when bulk-attaching captures to one or more checked wheels.
 *
 * @param {number} captureCount selected capture rows
 */
export const bulkAttachConfirmCopy = (captureCount = 0) => {
  const count = Math.max(0, Number(captureCount) || 0);
  return {
    title: "Attach to Wheel?",
    subtitle:
      count === 1
        ? "This capture will be attached to the wheels you selected."
        : `These ${count} captures will be attached to the wheels you selected.`,
    note: "They stay in your Capture inbox.",
    confirmLabel: "Attach",
  };
};

/**
 * Confirm copy when bulk-detaching captures from one or more checked wheels.
 *
 * @param {number} captureCount selected capture rows
 */
export const bulkDetachConfirmCopy = (captureCount = 0) => {
  const count = Math.max(0, Number(captureCount) || 0);
  return {
    title: "Detach from Wheel?",
    subtitle:
      count === 1
        ? "This capture will be removed from the wheels you selected. It stays in your Capture inbox."
        : `These ${count} captures will be removed from the wheels you selected. They stay in your Capture inbox.`,
    note: "You can attach them to a Wheel again at any time.",
    confirmLabel: "Detach",
  };
};

export const bulkDeleteResultMessage = ({ deletedIds = [], failedIds = [] } = {}) => {
  const ok = deletedIds.length;
  const bad = failedIds.length;
  if (bad === 0) {
    return {
      tone: "success",
      message: ok === 1 ? "Capture deleted." : `${ok} captures deleted.`,
    };
  }
  if (ok === 0) {
    return {
      tone: "error",
      message:
        bad === 1
          ? "Could not delete. Please try again."
          : `Could not delete ${bad} captures. Please try again.`,
    };
  }
  return {
    tone: "warning",
    message: `Deleted ${ok} of ${ok + bad} captures. The rest could not be deleted — please try again.`,
  };
};
